// Copy for the About area — a first pass at documenting what Loft is, drawn from
// PURPOSE.md and kept in plain voice. Structured so the page and the docs nav
// read from one source. Edit freely; this is where Loft explains itself.

export type AboutBlock =
  | { type: 'p'; text: string }
  | { type: 'h'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'dl'; items: { term: string; desc: string }[] }
  | { type: 'code'; lines: string[] };

export type AboutSection = {
  slug: string;
  title: string;
  blurb: string;
  blocks: AboutBlock[];
};

export const ABOUT_SECTIONS: AboutSection[] = [
  {
    slug: 'what-is-loft',
    title: 'What is Loft',
    blurb: 'Where we prototype, and how we keep the design system honest.',
    blocks: [
      {
        type: 'p',
        text: 'Loft is KareHero’s prototyping environment. It runs on the real MUI theme and the real llama components, so what you build here looks and behaves like the product.',
      },
      {
        type: 'p',
        text: 'It is also a design-system accountability layer. Every prototype is wired into inspect mode, which grades how each component earns its styling. You design first, then sweep.',
      },
      { type: 'h', text: 'What Loft is for' },
      {
        type: 'ul',
        items: [
          'Prototype fast, without policing design-system rules in the moment.',
          'Make design-system usage inspectable.',
          'Hand a build contract to engineers.',
          'Promote prototype work into llama.',
        ],
      },
    ],
  },
  {
    slug: 'getting-started',
    title: 'Getting started',
    blurb: 'From an empty branch to a prototype someone else can open.',
    blocks: [
      {
        type: 'p',
        text: 'Six steps. Loft generates its routes, menu and landing page from one manifest, so most of this is filling in files rather than wiring anything up.',
      },

      { type: 'h', text: '1. Get it running' },
      {
        type: 'p',
        text: 'Loft only deploys from a des/* branch, so start on one:',
      },
      { type: 'code', lines: ['git checkout -b des/your-prototype'] },
      {
        type: 'p',
        text: 'Then run /loft. It boots the app on localhost:3025, refreshes the caches inspect mode reads, and tells you if the manifest has a broken route or link.',
      },

      { type: 'h', text: '2. Add a screen' },
      {
        type: 'p',
        text: 'The component goes in its product-area folder, next to the screens it belongs with. Two edits register it: an entry in loft/projects.ts, and a line in loft/elements.tsx pointing at what renders. Routes, redirects, the side menu and the landing card all follow from those. There is no third list.',
      },
      {
        type: 'p',
        text: 'Add a co-located story while you are there. Anything that lays out a page gets a mobile and a desktop viewport.',
      },
      {
        type: 'p',
        text: 'When you later move or rename a screen, leave its legacy path in the manifest. That is what keeps links already sitting in Slack and Linear working.',
      },
      {
        type: 'p',
        text: 'The loft-scaffold agent does this wiring if you would rather not.',
      },

      { type: 'h', text: '3. Content and fixtures' },
      {
        type: 'p',
        text: 'Copy the product would really ship goes in a .content.ts file. Mock data standing in for an API response goes in .fixtures.ts. Both sit next to the screen.',
      },
      {
        type: 'p',
        text: 'This is not tidiness. The handover spec and the drill registry read the same modules the prototype does, so the spec cannot quietly drift from what you built.',
      },

      { type: 'h', text: '4. Scenes and explorations' },
      {
        type: 'p',
        text: 'A scene is one named state of a prototype, paired with the props that put it there. Define them in a .scenes.ts file and the state selector above the frame lists them. The choice is reflected in the URL as ?scene=, so every state is a link you can send. The handover reads the same catalog, so the states in the spec are the states in the prototype.',
      },
      {
        type: 'p',
        text: 'Explorations are different. Two takes on the same screen share a variantOf, each with its own label and a note saying what it is asking. Loft puts a switch above the frame so they can be compared side by side rather than opened one at a time. A version is narrower again: v1 against v2 of one idea, which the URL owns as /v2/.',
      },

      { type: 'h', text: '5. Sweep before you share' },
      {
        type: 'p',
        text: 'Turn on drill mode and work through what inspect flags, or hand it to the loft-reviewer agent. Coral is a violation to promote into the theme or a wrapped component. Orange is a decision waiting on you.',
      },
      {
        type: 'p',
        text: 'Do this once the design has settled, not while you are making it. Policing the rules in the moment is the thing Loft exists to avoid.',
      },

      { type: 'h', text: '6. Push to designs.dev' },
      {
        type: 'p',
        text: '/push-prototype commits, pushes the branch and deploys it. There is one designs.dev slot and the most recent des/* push takes it, so it checks who used it last and warns you before replacing their work.',
      },
      {
        type: 'p',
        text: 'Anyone you send the link to needs staging Kinde credentials. Prototypes are never public.',
      },
    ],
  },
  {
    slug: 'how-work-is-organised',
    title: 'How work is organised',
    blurb: 'What the master prototype is for, and how a project is laid out.',
    blocks: [
      { type: 'h', text: 'The master prototype' },
      {
        type: 'p',
        text: 'One entry sits above the rest. It is the member app mounted whole, so you open it and walk around rather than picking a screen off a list. Everything below it is work being done to it.',
      },
      {
        type: 'p',
        text: 'It exists so a project has something to be judged against. Comparing a proposal to the app as it stands is a different conversation from describing it in the abstract.',
      },
      {
        type: 'p',
        text: 'It should be one of two things, and which one needs saying out loud: the app in sync with main, or the app as it will be next. It is currently neither. It is kept matching production by hand, so it drifts as soon as nobody updates it. Treat it as a good approximation rather than a source of truth until that is fixed.',
      },

      { type: 'h', text: 'A project' },
      {
        type: 'p',
        text: 'Projects are the top level, matching the projects list on the ProdEng home page in Notion. Everything belonging to a piece of work sits inside its project: the exploration, the prototypes, the flows. Nothing is grouped by app surface, or by how far along it is.',
      },
      {
        type: 'p',
        text: 'Which surface a project touches is carried by its colour instead: the member app, CAM, both, or the partner portal. The key is on the landing page. A grey card is not product work at all, like the component playground. Grouping by surface would have buried work that spans two of them, which is most of the interesting work.',
      },
      {
        type: 'p',
        text: 'There is no lifecycle badge. There was one, nobody maintained it, and every card ended up asserting something that had stopped being true. Where a project has got to lives in Linear and Notion, which are the places people actually update.',
      },

      { type: 'h', text: 'All flows' },
      {
        type: 'p',
        text: 'A flows page collects the whole of a piece of work in one place: every screen, every state, in the order they happen. It is the first entry in a project, above the individual screens, because opening a project usually means wanting the whole thing rather than one screen of it.',
      },
      {
        type: 'p',
        text: 'That is the intent rather than the current state. Most projects have one. Tell Us Once and the IFA portal do not, so they open on their first screen instead.',
      },

      { type: 'h', text: 'Exploration' },
      {
        type: 'p',
        text: 'The exploration is the divergent end of a project: the brainstorm, everything on the table, before any of it is trimmed back. It is a page in its own right and it stays put once the work narrows, so reading a project back later shows what was considered and set aside, not just what survived.',
      },
      {
        type: 'p',
        text: 'A project in exploration still has a prototype in it. The two run alongside each other rather than one replacing the other.',
      },

      { type: 'h', text: 'Sandbox' },
      {
        type: 'p',
        text: 'Freestanding things that are not a piece of work: the component playground, device tests, whatever is being tried out. If it does not belong to a project, it goes here.',
      },
    ],
  },
  {
    slug: 'versions-and-concepts',
    title: 'Versions and concepts',
    blurb:
      'What gets built, what is still being explored, and how our numbers match the build.',
    blocks: [
      {
        type: 'p',
        text: 'Loft holds two kinds of work: what we are going to build, and what we are still exploring. On screen they look the same, so we name them differently.',
      },

      { type: 'h', text: 'The words' },
      {
        type: 'dl',
        items: [
          {
            term: 'Version',
            desc: 'Work that will be built. It has been signed off, or is heading there, and it is what engineering builds from.',
          },
          {
            term: 'Concept',
            desc: 'Work we are exploring. It is not for build, but it is fine to share for ideas.',
          },
          {
            term: 'Draft',
            desc: 'The next version while it is still being worked on, like 4.2 draft. It becomes 4.2 once it is signed off.',
          },
        ],
      },

      { type: 'h', text: 'Version numbers' },
      {
        type: 'p',
        text: 'A version has two numbers. The first moves for a major change: new tokens, new components, or a new shape to the flow. These need engineering to scope them. The second moves for smaller changes within what is already built.',
      },
      {
        type: 'p',
        text: 'Our numbers match the build. Onboarding is version 4 in the code, so in Loft it is 4.0 and 4.1. The onboarding before it is 3.0 and 3.1. Versions 1 and 2 came before this work and are not in Loft.',
      },

      { type: 'h', text: 'Concept numbers' },
      {
        type: 'p',
        text: 'Concepts get whole numbers: Concept 1, Concept 2, Concept 3. No decimals, so a concept can never be mistaken for a version.',
      },
      {
        type: 'p',
        text: 'When a concept is picked, it becomes the next version. It does not become Concept 2.1. The concept stays where it is, so the thinking behind the version is still there to look back on.',
      },

      { type: 'h', text: 'A versioned page' },
      {
        type: 'ul',
        items: [
          'Each piece of work has one page, and it opens on the latest version.',
          'The version in the breadcrumb opens the versions drawer. It lists every version, newest first, with a summary and what each one changed.',
          'Restore switches the page to that version. When you are not on the latest, the breadcrumb turns red, with a link back.',
          'Highlight changes, on the version you are looking at, marks what that version changed.',
          'A minor version only holds what it changed. It is drawn over its major’s screens, so nothing is copied and older versions cost nothing to keep.',
        ],
      },

      { type: 'h', text: 'Starting a new version' },
      {
        type: 'p',
        text: 'Add it to the page’s list in karehero-app/handover/onboarding-versions.tsx, layered on the version before, with a line for each change. A new major becomes the base the next minors are layered on, and the old major stays in the list to switch back to.',
      },

      { type: 'h', text: 'Still open' },
      {
        type: 'ul',
        items: [
          'What counts as sign-off: a design review round, or engineering taking it into a sprint.',
          'Whether a version gets its number at sign-off or when it ships.',
          'How each version links to its work in Linear.',
        ],
      },
    ],
  },
  {
    slug: 'inspect-drill',
    title: 'Inspect & drill mode',
    blurb: 'Opening it, reading what it tells you, and the states underneath.',
    blocks: [
      {
        type: 'p',
        text: 'Drill mode is the engineer’s view of a prototype. It grades every component against one rule: styling is earned in exactly one of three ways.',
      },
      {
        type: 'ul',
        items: [
          'Pipe: raw MUI. The theme already styles it.',
          'Wrapped: a reusable component owns the styling.',
          'Theme: a global token change, deliberate and rare.',
        ],
      },
      {
        type: 'p',
        text: 'There are no per-component overrides. A live sx on an instance is flagged in coral as a violation. The fix is to promote it into the theme or into a wrapped component.',
      },

      { type: 'h', text: 'Opening it' },
      {
        type: 'p',
        text: 'The pill above the frame has two segments. The eye is preview, the code icon is drill mode. Where a stage offers it, a select sits alongside for the viewport: the device frame, a full-bleed fill, or a fixed breakpoint width.',
      },
      {
        type: 'p',
        text: 'In drill mode, hovering a component outlines it and grades it. Clicking pins it, so the panel holds still while you read. The layers tree beside the frame is the same selection from the other end, read live from the React tree rather than from anything written down.',
      },
      {
        type: 'p',
        text: 'What you are looking at is an address. Drill mode is ?drill=1, the pinned component is ?select=, and they sit alongside ?scene=. So one component, in one state, drilled, is a link you can send. The copy button in the panel puts that URL, the component name and its source file on the clipboard, which is what to paste when you want someone or Claude to look at the same thing.',
      },

      { type: 'h', text: 'Reading the panel' },
      {
        type: 'p',
        text: 'The grade comes first, then whatever else is true of that component:',
      },
      {
        type: 'dl',
        items: [
          {
            term: 'Feature',
            desc: 'A composition scoped to one screen rather than a reusable component. Same styling axis underneath, but it graduates into the design system through its handover, not on its own.',
          },
          {
            term: 'Proposed',
            desc: 'A theme change or a variant that exists in the prototype theme and not in llama. Shipping it lands on every instance everywhere, which is the gate.',
          },
          {
            term: 'Drift',
            desc: 'A wrapped component whose name also exists in llama. A fork that may have diverged or been superseded. The panel names what changed at the props level. This is the actionable one.',
          },
          {
            term: 'Violations',
            desc: 'How many live sx declarations are on the instance, with the offending lines on request.',
          },
        ],
      },
      {
        type: 'p',
        text: 'Structure, props and what is passed as children are read live, so they cannot go stale. Only the value-add is written down: the tokens a component relies on, why it exists, and its changelog.',
      },
      {
        type: 'p',
        text: 'Proposed and shipped are worked out by diffing against llama on main, so the panel also says whether your branch is in sync. If it says behind main, the shipped verdicts are stale and nothing should be deleted on the strength of them.',
      },

      { type: 'h', text: 'States' },
      {
        type: 'p',
        text: 'The select beside the drill toggle lists every state a prototype can be in, grouped the way the handover groups them: views, happy path, empty, edge cases, errors. Picking one remounts the prototype in that state and writes it into the URL.',
      },
      {
        type: 'p',
        text: 'A state records four things: an id, a label, which of those groups it belongs to, and the props that put the prototype there. A fifth is worth adding, a line saying how you reach it in the real product, because the handover prints it as the reproduction step.',
      },
      {
        type: 'p',
        text: 'The happy path is the least useful one to write. Everyone already pictures it, and it is the one that gets built correctly anyway. The states worth the effort are the empty one, the one where the data is wrong, the error, and the halfway-through. Those are the ones that get skipped in a build and found in QA.',
      },
      {
        type: 'p',
        text: 'Write them as states the real thing can be in, not as demos of the design. The handover reads the same list, so a state nobody writes is a state nobody builds.',
      },
    ],
  },
  {
    slug: 'claude-tooling',
    title: 'Claude tooling',
    blurb:
      'The scope lock, commands and agents that shape how Claude works here.',
    blocks: [
      {
        type: 'p',
        text: 'Loft work happens on a des/* branch. Claude Code reads its conventions from this repo, so there is nothing to switch on before you start.',
      },

      { type: 'h', text: 'Scope lock' },
      {
        type: 'p',
        text: 'A PreToolUse hook confines work to front-end/designs/ whenever the repo is on a des/* branch. The branch is the state. Checking out a ticket branch lifts the lock.',
      },
      {
        type: 'p',
        text: 'It blocks file edits on any path outside front-end/designs/, and these commands, which write across the repo:',
      },
      {
        type: 'ul',
        items: [
          'bin/kh prettier:fix. Run prettier scoped to the designs package instead.',
          'pnpm install and bin/kh deps. They rewrite the root lockfile.',
          'bin/kh api:gen. Prototypes mock their data in fixtures.',
          'git add -A and git commit -a. They stage whatever is left outside Loft.',
          'Bare git push --force. Use --force-with-lease.',
          'Pushes to main.',
        ],
      },
      {
        type: 'p',
        text: 'If Claude hits a block, the change either belongs inside Loft or belongs on a ticket branch. Working around it is not the answer.',
      },
      {
        type: 'p',
        text: 'To lift the lock without leaving the branch, for a design-system change that has to land in llama:',
      },
      { type: 'code', lines: ['touch ~/.claude/karehero-loft-unlock'] },
      { type: 'p', text: 'Delete the file to restore it.' },

      { type: 'h', text: 'Commands' },
      {
        type: 'dl',
        items: [
          {
            term: '/loft',
            desc: 'Boots the environment on localhost:3025, refreshes the caches inspect mode reads, and checks the manifest for broken routes and links.',
          },
          {
            term: '/push-prototype',
            desc: 'Commits and pushes the des/* branch, which deploys it to designs.dev. Warns first if someone else is using the slot.',
          },
        ],
      },

      { type: 'h', text: 'Agents' },
      {
        type: 'p',
        text: 'Two subagents for work that is bounded and better done out of the way. Both hand back a report, and neither takes over the design loop.',
      },
      {
        type: 'dl',
        items: [
          {
            term: 'loft-reviewer',
            desc: 'Sweeps for sx violations, hardcoded colour, forks of llama components, redundant theme overrides, dead modules and manifest drift. Groups what it finds by action. Reports, never fixes.',
          },
          {
            term: 'loft-scaffold',
            desc: 'Wires a new screen through projects.ts and elements.tsx, with its content, fixtures and story, then runs the route check and typecheck. Plumbing only, it does not design the screen.',
          },
        ],
      },

      { type: 'h', text: 'Where the rules live' },
      {
        type: 'p',
        text: 'Not in a command. CLAUDE.md in this folder loads automatically whenever Claude touches a file here, and links PURPOSE.md and lib/llama/CONVENTIONS.md. Anything that restates them is a copy that will drift.',
      },

      { type: 'h', text: 'Installing' },
      {
        type: 'p',
        text: 'Claude Code only reads skills, agents and hooks from your home folder, never from a repo. The versions checked in under front-end/designs/skills are the shared ones, and this copies them across:',
      },
      { type: 'code', lines: ['bash front-end/designs/skills/install.sh'] },
      {
        type: 'p',
        text: 'Run it once to get set up, and again after anyone edits a skill. It prints the one line the hook needs in your settings.json, and says so only if it is missing. Restart Claude Code afterwards.',
      },
    ],
  },
];
